import { assertNormalizedChartIntegrity } from './chartIntegrity.js';
import { assertCompletePalaceStemTransformations } from './transformationEvidence.js';
import type { LocalChartRepository } from './localPersistence.js';
import {
  inspectLocalChartTransfer,
  restoreLocalChartPrivateBackup,
  validatedLocalChartPrivateBackupRecords,
  type LocalChartRestoreResult,
  type LocalChartTransferInspection,
} from './localChartTransfer.js';

export type ProductionLocalImportPreflight =
  | Readonly<{
      status: 'ready';
      inspection: Extract<LocalChartTransferInspection, { status: 'valid_private_backup' }>;
      requiresConfirmation: true;
      networkRequired: false;
      disclosure: string;
    }>
  | Readonly<{
      status: 'not_restorable';
      inspection: LocalChartTransferInspection;
      requiresConfirmation: false;
      networkRequired: false;
      disclosure: string;
    }>
  | Readonly<{
      status: 'invalid_chart_data';
      inspection: Extract<LocalChartTransferInspection, { status: 'valid_private_backup' }>;
      requiresConfirmation: false;
      networkRequired: false;
      disclosure: string;
    }>;

export function preflightProductionLocalChartImport(input: string | unknown): ProductionLocalImportPreflight {
  const inspection = inspectLocalChartTransfer(input);
  if (inspection.status !== 'valid_private_backup') {
    return Object.freeze({
      status: 'not_restorable',
      inspection,
      requiresConfirmation: false as const,
      networkRequired: false as const,
      disclosure: inspection.status === 'valid_redacted_share'
        ? '此檔案是隱私分享版，刻意不可還原；未寫入任何本機資料。'
        : '檔案未通過正式匯入預檢；未寫入任何本機資料。',
    });
  }

  try {
    const records = validatedLocalChartPrivateBackupRecords(input);
    for (const record of records) {
      assertNormalizedChartIntegrity(record.chart);
      assertCompletePalaceStemTransformations(record.chart);
    }
  } catch {
    return Object.freeze({
      status: 'invalid_chart_data',
      inspection,
      requiresConfirmation: false as const,
      networkRequired: false as const,
      disclosure: '私密備份的檔案格式雖可辨識，但其中至少一筆命盤未通過正式完整性驗證；整包不允許匯入。',
    });
  }

  return Object.freeze({
    status: 'ready',
    inspection,
    requiresConfirmation: true as const,
    networkRequired: false as const,
    disclosure: `已完成 ${inspection.itemCount} 筆命盤的格式與完整性預檢；確認後才會寫入此瀏覽器本機資料。`,
  });
}

export function executeProductionLocalChartImport(
  repository: LocalChartRepository,
  input: string | unknown,
  confirmed: boolean,
): LocalChartRestoreResult {
  const preflight = preflightProductionLocalChartImport(input);
  if (preflight.status !== 'ready') throw new Error(`Production local import is not ready: ${preflight.status}`);
  if (!confirmed) throw new Error('Production local import requires explicit confirmation before writing');
  return restoreLocalChartPrivateBackup(repository, input);
}
